import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePopper } from "react-popper";

import {
	faBoxArchive,
	faCheckSquare,
	faClock,
	faEllipsisV,
	faPen,
	faSquare,
	faTrashCan,
	faUser,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import ActionItemAssignees from "~/actionItems/ActionItemAssignees";
import { DUE_DATE_VIEW, REASSIGN_VIEW, Views } from "~/actionItems/constants";
import { CopyToModalRenderer } from "~/actionItems/CopyToModal";
import { ActionItemsContext } from "~/actionItems/newActionItems/ActionItemsContext";
import type { ActionItem } from "~/actionItems/types";
import { DueDate } from "~/common";
import AppContext from "~/common/AppContext";
import ViewportContext from "~/common/ViewportContext";
import { inVideocallMode, isMobile, isTeams } from "~/lib/utils";
import AssignableDropdown, {
	type AssignableDropdownItemType,
	type AssignableDropdownMenuSections,
	getCopyToMenuItem,
	getCreatorMenuItem,
	getDueDateItem,
	getLazyReassignMenuItem,
} from "~/notes/AssignableDropdown";
import { Button, useModals } from "~/ui";
import { IconButton } from "~/ui/button/IconButton";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import Popup from "~/ui/legacy/Popup";

import { ActionItemsTabContext } from "./ActionItemsTab";
import { NoteBlockStatus, StatusCondition } from "./enum";

import styles from "./ActionItemActions.module.css";

function getAnimatedParent(from: HTMLElement): HTMLElement | null {
	const el = from.closest("data-action-items-animated-layout");
	if (el instanceof HTMLElement) return el;
	return null;
}

function splitCompletedByAnyAndAll(
	actionItems: Array<ActionItem>,
	user: UserAttrs | undefined,
	status: NoteBlockStatus,
) {
	const itemsCompletedByAny: Array<string> = [];
	const itemsCompletedByAll: Array<{
		id: string;
		assignees: Array<{ id: string; status: NoteBlockStatus | null }>;
	}> = [];
	actionItems.forEach(actionItem => {
		if (actionItem.statusCondition === StatusCondition.SINGLE) {
			itemsCompletedByAny.push(actionItem.id);
			return;
		}
		const assignees: Array<{ id: string; status: NoteBlockStatus | null }> = [];
		let userIsAssignee = false;
		actionItem.assignments?.forEach(assignment => {
			if (assignment.assignee.id === user?.id) {
				userIsAssignee = true;
				assignees.push({
					id: assignment.assignee.id,
					status,
				});
				return;
			}
			assignees.push({
				id: assignment.assignee.id,
				status: assignment.status as NoteBlockStatus,
			});
		});
		if (!userIsAssignee) return;
		itemsCompletedByAll.push({
			id: actionItem.id,
			assignees,
		});
	}, []);
	return { itemsCompletedByAny, itemsCompletedByAll };
}

type ActionItemMetaProps = {
	item: ActionItem;
	withAssignee?: boolean;
	editingDisabled?: boolean;
	onSelect(): void;
	onStartEditing(force?: boolean): void;
	className?: string;
	showDueDate?: boolean;
	selected?: boolean;
};

export default function ActionItemActions({
	item,
	withAssignee,
	editingDisabled,
	onSelect,
	onStartEditing,
	className,
	showDueDate,
	selected,
}: ActionItemMetaProps) {
	const {
		id,
		note,
		dueDate,
		done,
		wontDo,
		hasAccessToNote,
		creator,
		assignments,
		hasOriginalBlock,
		originalBlock,
		completedBy,
	} = item;
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [subview, setSubview] = useState<string>();
	const { isTablet } = useContext(ViewportContext);
	const { user } = useContext(AppContext);
	const actionsRef = useRef<HTMLDivElement>(null);
	const {
		selectedActionItems,
		setSelectedActionItems,
		onMarkItemsAsWontDo,
		onMarkItemsAsCompleted,
		onMarkItemsAsIncompleted,
		onUpdateItemsAttrs,
		onDeleteItem,
		onDeleteItems,
		hasAccessToDueDates,
		filters,
		view,
		inPanel,
		hasAccessToMultipleAssignees,
		allowCompleteByAny,
		renderingContext,
	} = useContext(ActionItemsContext);
	const { streamId } = useContext(ActionItemsTabContext);
	const { showCompleted, showWontDo, showAIDetected } = filters;
	const modals = useModals();

	const inSeriesPanel = renderingContext === "seriesPanel";

	useEffect(() => {
		if (!isOpened) return;
		const el = document.createElement("div");

		let animatedParent: HTMLElement | null = null;
		if (isTablet && actionsRef.current) {
			actionsRef.current.appendChild(el);
			animatedParent = getAnimatedParent(actionsRef.current);
		} else {
			document.body.appendChild(el);
		}

		// FIXME: This is a disgusting hack to prevent the assignable dropdown from
		// being covered by other elements. Since we're animating the layout, there
		// is a transform: translate3d() applied to the item we're rendered in
		// here. Since the portal is inside the action item on non-mobile
		// platforms, the dropdown is affected and ends up somehow beneath the
		// following action items.
		//
		// To fix this, we set transform to none on the element when the menu is
		// open, and then stop overriding transform when it's closed.
		if (animatedParent) {
			animatedParent.classList.add(styles.forceNoTransform);
		}
		return () => {
			// Part of the aforementioned hack:
			if (animatedParent) animatedParent.classList.remove(styles.forceNoTransform);

			el.remove();
		};
	}, [isOpened, actionsRef, isTablet]);

	const onOpen = useCallback(
		(e: React.MouseEvent, subview?: string) => {
			e.stopPropagation();
			e.preventDefault();
			if (!selectedActionItems.some(selectedActionItem => selectedActionItem.id === id)) {
				setSelectedActionItems?.([]);
			}
			setSubview(subview);
			setIsOpened(true);
		},
		[id, selectedActionItems, setSelectedActionItems],
	);

	const onDueDateOpen = useCallback(
		(e: React.MouseEvent) => {
			onOpen(e, DUE_DATE_VIEW);
		},
		[onOpen],
	);

	const onAssigneeOpen = useCallback(
		(e: React.MouseEvent) => {
			onOpen(e, !editingDisabled && view !== Views.TEAMMATES ? REASSIGN_VIEW : undefined);
		},
		[editingDisabled, view, onOpen],
	);

	const onCloseDropdown = useCallback(() => {
		setIsOpened(false);
	}, []);

	// TODO: For now this will say that the assignee is external if at least one of the assignees is external
	// We should change that when the reassign section supports multiple assignee
	const assigneeIsExternal = useMemo(() => {
		if (!note || !assignments || !("noteuserSet" in note)) return false;
		const noteUsersSet = new Set(note.noteuserSet.map(noteUser => noteUser.id));
		return assignments.some(assignment => noteUsersSet.has(assignment.assignee.id));
	}, [note, assignments]);

	const actionsOnlyForCurrentItem = useMemo(() => {
		return selectedActionItems.length === 0 || (selectedActionItems.length === 1 && selectedActionItems[0].id === id);
	}, [selectedActionItems, id]);

	const completeByAllAndNotAssignee = useMemo(() => {
		return (
			item.statusCondition === StatusCondition.ALL &&
			!Boolean(item.assignments?.some(assignment => assignment.assignee.id === user?.id))
		);
	}, [item, user]);

	const userMarkedSelectedAsWontDo = useMemo(
		() =>
			Boolean(
				item.assignments?.some(
					assignment => assignment.assignee.id === user?.id && assignment.status === NoteBlockStatus.WONT_DO,
				),
			),
		[item, user],
	);

	const hasAccessToAllSelectedNotes = useMemo(
		() =>
			selectedActionItems.every(
				// TODO: Check for a better way to verify teammates' todo streams
				selectedActionItem => selectedActionItem.hasAccessToNote || !selectedActionItem.note?.url,
			),
		[selectedActionItems],
	);

	const noNotesAreLocked = useMemo(
		() =>
			selectedActionItems.length === 0
				? !note?.isLocked
				: selectedActionItems.every(selectedActionItem => !selectedActionItem.note?.isLocked),
		[selectedActionItems, note],
	);

	const disableWontDoMenuItem = useMemo(() => {
		return (
			(actionsOnlyForCurrentItem && wontDo) ||
			(!actionsOnlyForCurrentItem && selectedActionItems.every(selectedActionItem => selectedActionItem.wontDo))
		);
	}, [actionsOnlyForCurrentItem, wontDo, selectedActionItems]);

	const showWontDoMenuItem = useMemo(() => {
		return (
			hasAccessToAllSelectedNotes &&
			(!actionsOnlyForCurrentItem || !wontDo) &&
			(!actionsOnlyForCurrentItem || (!completeByAllAndNotAssignee && !userMarkedSelectedAsWontDo))
		);
	}, [
		hasAccessToAllSelectedNotes,
		actionsOnlyForCurrentItem,
		wontDo,
		completeByAllAndNotAssignee,
		userMarkedSelectedAsWontDo,
	]);

	const disableDoneMenuItem = useMemo(() => {
		return selectedActionItems.every(selectedActionItem => selectedActionItem.done) || completeByAllAndNotAssignee;
	}, [selectedActionItems, completeByAllAndNotAssignee]);

	const showDoneMenuItem = useMemo(() => {
		return hasAccessToAllSelectedNotes && !actionsOnlyForCurrentItem;
	}, [hasAccessToAllSelectedNotes, actionsOnlyForCurrentItem]);

	const disableIncompleteMenuItem = useMemo(() => {
		return (
			!actionsOnlyForCurrentItem &&
			selectedActionItems.every(selectedActionItem => !selectedActionItem.wontDo && !selectedActionItem.done) &&
			!completeByAllAndNotAssignee
		);
	}, [selectedActionItems, actionsOnlyForCurrentItem, completeByAllAndNotAssignee]);

	const showIncompleMenuItem = useMemo(() => {
		return (
			hasAccessToAllSelectedNotes &&
			(!actionsOnlyForCurrentItem || (!showWontDoMenuItem && !completeByAllAndNotAssignee))
		);
	}, [hasAccessToAllSelectedNotes, actionsOnlyForCurrentItem, showWontDoMenuItem, completeByAllAndNotAssignee]);

	const markAsWontDoMenuItem = useMemo(() => {
		if (!showWontDoMenuItem) return null;
		return {
			id: "toggle-wont-do",
			icon: faBoxArchive,
			className: cx({ [styles.disabledMenuItem]: disableWontDoMenuItem }),
			right: (
				<>
					Archive
					{selectedActionItems.length > 1 ? (
						<>
							<b> ({selectedActionItems.length})</b> action items
						</>
					) : (
						" action item"
					)}
				</>
			),
			disabled: disableWontDoMenuItem,
			action(close: () => void) {
				const { itemsCompletedByAny, itemsCompletedByAll } = splitCompletedByAnyAndAll(
					actionsOnlyForCurrentItem ? [item] : selectedActionItems,
					user,
					NoteBlockStatus.WONT_DO,
				);
				if (itemsCompletedByAny.length > 0) {
					onMarkItemsAsWontDo(itemsCompletedByAny);
				}
				itemsCompletedByAll.forEach(itemCompletedByAll => {
					onUpdateItemsAttrs([itemCompletedByAll.id], { assignees: itemCompletedByAll.assignees });
				});
				close();
				onCloseDropdown();
				return true;
			},
		};
	}, [
		item,
		showWontDoMenuItem,
		selectedActionItems,
		disableWontDoMenuItem,
		actionsOnlyForCurrentItem,
		onMarkItemsAsWontDo,
		onUpdateItemsAttrs,
		onCloseDropdown,
		user,
	]);

	const markAsDoneMenuItem = useMemo(() => {
		if (!showDoneMenuItem) return null;
		return {
			id: "toggle-done",
			icon: faCheckSquare,
			className: cx({ [styles.disabledMenuItem]: disableDoneMenuItem }),
			right: <>Mark {<b>({selectedActionItems.length})</b>} as Complete</>,
			disabled: disableDoneMenuItem,
			action(close: () => void) {
				const { itemsCompletedByAny, itemsCompletedByAll } = splitCompletedByAnyAndAll(
					selectedActionItems,
					user,
					NoteBlockStatus.DONE,
				);
				if (itemsCompletedByAny.length > 0) {
					onMarkItemsAsCompleted(itemsCompletedByAny);
				}
				itemsCompletedByAll.forEach(itemCompletedByAll => {
					onUpdateItemsAttrs([itemCompletedByAll.id], { assignees: itemCompletedByAll.assignees });
				});
				close();
				onCloseDropdown();
				return true;
			},
		};
	}, [
		showDoneMenuItem,
		selectedActionItems,
		disableDoneMenuItem,
		onMarkItemsAsCompleted,
		onUpdateItemsAttrs,
		onCloseDropdown,
		user,
	]);

	const markAsIncompleteMenuItem = useMemo(() => {
		if (!showIncompleMenuItem) return null;
		return {
			id: "toggle-incomplete",
			icon: faSquare,
			className: cx({ [styles.disabledMenuItem]: disableIncompleteMenuItem }),
			right: <>Mark {selectedActionItems.length > 1 && <b>({selectedActionItems.length})</b>} as Incomplete</>,
			disabled: disableIncompleteMenuItem,
			action(close: () => void) {
				const { itemsCompletedByAny, itemsCompletedByAll } = splitCompletedByAnyAndAll(
					actionsOnlyForCurrentItem ? [item] : selectedActionItems,
					user,
					NoteBlockStatus.NOT_DONE,
				);
				if (itemsCompletedByAny.length > 0) {
					onMarkItemsAsIncompleted(itemsCompletedByAny);
				}
				itemsCompletedByAll.forEach(itemCompletedByAll => {
					onUpdateItemsAttrs([itemCompletedByAll.id], { assignees: itemCompletedByAll.assignees });
				});
				close();
				onCloseDropdown();
				return true;
			},
		};
	}, [
		item,
		showIncompleMenuItem,
		selectedActionItems,
		disableIncompleteMenuItem,
		actionsOnlyForCurrentItem,
		onMarkItemsAsIncompleted,
		onUpdateItemsAttrs,
		onCloseDropdown,
		user,
	]);

	const editItemMenuItem = useMemo(() => {
		if (editingDisabled || selectedActionItems.length > 1 || note?.stream?.__typename !== "NoteStream") {
			return null;
		}
		const editItemText = <div>Edit action item</div>;
		return {
			id: "edit",
			disabled: note?.isLocked,
			className: cx({ [styles.disabledMenuItem]: note?.isLocked }),
			action: (close: () => void) => {
				onSelect();
				onStartEditing(true);
				close();
				onCloseDropdown();
				return true;
			},
			icon: faPen,
			right: note?.isLocked ? (
				<Popup
					inverted
					on="hover"
					position="bottom center"
					content="You cannot do this while the note is locked."
					className={styles.tooltip}
					trigger={editItemText}
				/>
			) : (
				editItemText
			),
		};
	}, [editingDisabled, selectedActionItems, note, onSelect, onStartEditing, onCloseDropdown]);

	const copyMenuItem = useMemo(() => {
		if (!hasAccessToAllSelectedNotes) return null;
		const actionItems = actionsOnlyForCurrentItem ? [item] : selectedActionItems;
		const hasPrivateNoteInOneOfItems = actionItems.some(item => item.note && item.note.type === "private");
		if (hasPrivateNoteInOneOfItems) return null;
		return {
			...getCopyToMenuItem(actionItems.length),
			action: (close: () => void) => {
				close();
				onCloseDropdown();
				modals.open(({ close }) => (
					<CopyToModalRenderer
						ids={actionItems.map(item => item.id)}
						showAiDetected={showAIDetected}
						showCompleted={showCompleted}
						showWontDo={showWontDo}
						teammatesView={false}
						forStream={view !== Views.STREAM_TAB ? undefined : streamId}
						close={close}
					/>
				));
				return true;
			},
		};
	}, [
		hasAccessToAllSelectedNotes,
		view,
		actionsOnlyForCurrentItem,
		selectedActionItems,
		modals,
		item,
		onCloseDropdown,
		showCompleted,
		showWontDo,
		streamId,
		showAIDetected,
	]);

	const deleteItemMenuItem = useMemo(() => {
		if (!hasAccessToAllSelectedNotes) {
			return null;
		}
		const deleteActionItemText = (
			<div>
				Delete {!actionsOnlyForCurrentItem && <b>({selectedActionItems.length})</b>} action item
				{!actionsOnlyForCurrentItem && "s"}
			</div>
		);
		return {
			id: "delete",
			disabled: !noNotesAreLocked,
			className: cx({ [styles.disabledMenuItem]: !noNotesAreLocked }),
			action: (close: () => void) => {
				if (actionsOnlyForCurrentItem) {
					onDeleteItem?.(item);
				} else {
					onDeleteItems?.(selectedActionItems.map(item => item.id));
				}
				close();
				onCloseDropdown();
				return true;
			},
			icon: faTrashCan,
			right: !noNotesAreLocked ? (
				<Popup
					inverted
					on="hover"
					position="bottom center"
					content="You cannot do this while the note is locked"
					className={styles.tooltip}
					trigger={deleteActionItemText}
				/>
			) : (
				deleteActionItemText
			),
		};
	}, [
		hasAccessToAllSelectedNotes,
		actionsOnlyForCurrentItem,
		selectedActionItems,
		noNotesAreLocked,
		item,
		onDeleteItem,
		onDeleteItems,
		onCloseDropdown,
	]);

	const generateDueDateSection = useCallback(
		(close: () => void) => {
			if (!hasAccessToAllSelectedNotes) return null;
			const actionItems = actionsOnlyForCurrentItem ? [item] : selectedActionItems;
			return getDueDateItem({
				hasAccessToDuedates: hasAccessToDueDates,
				setBlocksAttrs: (updates: unknown) => {
					onUpdateItemsAttrs(
						actionItems.map(item => item.id),
						updates,
					);
					onCloseDropdown();
				},
				dueDates: actionItems.map(item => item.dueDate),
				close: () => {
					close();
					onCloseDropdown();
				},
				hasAccessToOriginalNote: actionItems.every(item => !!item.originalBlock || !item.hasOriginalBlock),
				originalBlockIds: actionItems.map(item => item.originalBlock?.id ?? null),
				blockIdsToNoteIds: actionItems.reduce(
					(acc, item) => {
						if (!item.originalBlock?.id) return acc;
						return { ...acc, [item.originalBlock?.id]: item.originalBlock.noteId };
					},
					{} as { [key: string]: string },
				),
				isTeams,
			});
		},
		[
			item,
			hasAccessToAllSelectedNotes,
			hasAccessToDueDates,
			selectedActionItems,
			actionsOnlyForCurrentItem,
			onUpdateItemsAttrs,
			onCloseDropdown,
		],
	);

	const reassignSection = useMemo(() => {
		return getLazyReassignMenuItem({
			updateItemAttrs: (updates: unknown) => {
				onUpdateItemsAttrs(actionsOnlyForCurrentItem ? [id] : selectedActionItems.map(item => item.id), updates);
			},
			// @ts-ignore
			currentAssignee:
				assignments && assignments.length > 0
					? { ...assignments[0].assignee, isExternalInNote: assigneeIsExternal }
					: undefined,
			currentAssigneesFromParticipants: assignments?.map(a => a.assignee),
			item: {
				...item,
				assignees:
					item.assignments?.map(a => {
						return {
							id: a.assignee.id,
							status: a.status ? (a.status as NoteBlockStatus) : NoteBlockStatus.NOT_DONE,
						};
					}) ?? [],
				statusCondition: item.statusCondition ? StatusCondition[item.statusCondition] : StatusCondition.SINGLE,
			},
			note,
			// @ts-ignore
			stream: note?.stream || { displayName: "", isTodoStream: false, type: "oneonone" },
			user,
			showHeading: true,
			hasAccessToOriginalNote: (hasAccessToNote && !hasOriginalBlock) || !!originalBlock,
			disabled: !actionsOnlyForCurrentItem || !hasAccessToAllSelectedNotes,
			originalBlockId: originalBlock?.id || null,
			isTeams,
			itemHasActiveIntegration: Boolean(item.actionItemIntegrationLink || item.asanablocklink),
			hasAccessToMultipleAssignees,
			allowCompleteByAny,
			completedBy,
			done,
		});
	}, [
		actionsOnlyForCurrentItem,
		hasAccessToAllSelectedNotes,
		onUpdateItemsAttrs,
		assignments,
		originalBlock,
		hasAccessToNote,
		hasOriginalBlock,
		item,
		note,
		id,
		selectedActionItems,
		user,
		assigneeIsExternal,
		hasAccessToMultipleAssignees,
		allowCompleteByAny,
		completedBy,
		done,
	]);

	const markItemSection = useMemo(() => {
		const items: Array<AssignableDropdownItemType> = [];
		if (markAsWontDoMenuItem) {
			items.push(markAsWontDoMenuItem);
		}
		if (markAsDoneMenuItem) {
			items.push(markAsDoneMenuItem);
		}
		if (markAsIncompleteMenuItem) {
			items.push(markAsIncompleteMenuItem);
		}
		return items;
	}, [markAsWontDoMenuItem, markAsDoneMenuItem, markAsIncompleteMenuItem]);

	const copyEditDeleteSection = useMemo(() => {
		const items: Array<AssignableDropdownItemType> = [];
		if (editItemMenuItem) {
			items.push(editItemMenuItem);
		}
		if (copyMenuItem) {
			items.push(copyMenuItem);
		}
		if (deleteItemMenuItem) {
			items.push(deleteItemMenuItem);
		}
		return items;
	}, [editItemMenuItem, copyMenuItem, deleteItemMenuItem]);

	const creatorMenuSection = useMemo(() => {
		const actionItems = actionsOnlyForCurrentItem ? [item] : selectedActionItems;
		const hasPrivateNoteInOneOfItems = actionItems.some(item => item.note.type === "private");
		if (hasPrivateNoteInOneOfItems) return null;
		return getCreatorMenuItem({ user, creator, isAiGenerated: item.aiGenerated });
	}, [user, creator, actionsOnlyForCurrentItem, item, selectedActionItems]);

	const menuSections = useCallback(
		(close: () => void) => {
			const sections: AssignableDropdownMenuSections = [];
			const dueDateSection = generateDueDateSection(close);
			if (dueDateSection) {
				sections.push({ children: [dueDateSection] });
			}
			if (reassignSection) {
				sections.push({ children: [reassignSection] });
			}
			if (markItemSection.length > 0) {
				sections.push({ children: markItemSection });
			}
			if (copyEditDeleteSection.length > 0) {
				sections.push({ children: copyEditDeleteSection });
			}
			if (creatorMenuSection) {
				sections.push({ children: [creatorMenuSection] });
			}
			return sections;
		},
		[reassignSection, generateDueDateSection, markItemSection, copyEditDeleteSection, creatorMenuSection],
	);

	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
	const [ellipsis, setEllipsis] = useState<HTMLButtonElement | null>(null);

	const { styles: popperStyles, attributes } = usePopper(ellipsis, popperElement, {
		placement: "auto",
	});

	const mobileActions = useMemo(
		() =>
			selected || isOpened ? (
				<IconButton
					icon={faEllipsisV}
					className={cx(styles.actionsKebab, {
						[styles.editing]: isOpened,
						[styles.inPanel]: inPanel || inSeriesPanel,
					})}
					onClickCapture={onOpen}
					ref={setEllipsis}
					data-action-trigger
				/>
			) : (
				showDueDate &&
				dueDate && (
					<DueDate
						done={done || wontDo}
						date={dueDate}
						className={cx("time", styles.duedate, { [styles.doneOrWontDo]: done || wontDo })}
						short
					/>
				)
			),
		[done, dueDate, inPanel, inSeriesPanel, isOpened, onOpen, selected, showDueDate, wontDo],
	);

	const panelActions = useMemo(
		() => (
			<>
				{!isOpened && showDueDate && dueDate && !inSeriesPanel && (
					<DueDate done={done || wontDo} date={dueDate} className="duedateInPanel" onClick={onDueDateOpen} short />
				)}
				<IconButton
					icon={faEllipsisV}
					size="small"
					variant="tertiary"
					className={cx("actionsKebabInPanel", { [styles.editing]: isOpened, ["panelKebabMenuOpen"]: isOpened })}
					onClickCapture={onOpen}
					ref={setEllipsis}
					data-action-trigger
				/>
			</>
		),
		[done, dueDate, inSeriesPanel, isOpened, onDueDateOpen, onOpen, showDueDate, wontDo],
	);

	const stopPropagatingActionsClicks = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		event.stopPropagation();
	}, []);

	const narrowScreen = isMobile || inVideocallMode;
	const inRecording = view === Views.RECORDING;

	return (
		<div
			className={cx(styles.actions, className, { [styles.inRecording]: inRecording })}
			ref={actionsRef}
			data-action-item-actions
			onClick={stopPropagatingActionsClicks}
		>
			<div className={cx("actionsTrigger", styles.actionsTrigger, { isOpened: isOpened })}>
				{narrowScreen ? (
					mobileActions
				) : inPanel || inSeriesPanel ? (
					panelActions
				) : (
					<>
						{!inRecording && (
							<span className={cx("dueDateContainer", styles.dueDateContainer, { [styles.editing]: selected })}>
								{dueDate ? (
									<DueDate
										as={Button}
										done={done || wontDo}
										date={dueDate}
										className={cx("time", styles.duedate, { [styles.doneOrWontDo]: done || wontDo })}
										onClick={onDueDateOpen}
									/>
								) : note?.stream && note.stream.__typename === "NoteStream" ? (
									<IconButton
										size="small"
										variant="tertiary"
										icon={faClock}
										className={styles.actionsClock}
										onClick={onDueDateOpen}
									/>
								) : null}
							</span>
						)}
						{withAssignee && (
							<Button
								size="wide"
								className={cx("actionsAssignee", styles.actionsAssignee, { [styles.editing]: selected })}
								onClick={onAssigneeOpen}
							>
								{assignments && assignments.length > 0 ? (
									<ActionItemAssignees assignments={assignments} note={note} />
								) : (
									<FontAwesomeIcon icon={faUser} />
								)}
							</Button>
						)}
						<IconButton
							icon={faEllipsisV}
							size="small"
							variant="tertiary"
							className={cx(styles.actionsKebab, {
								[styles.editing]: isOpened,
								actionsKebabInRecording: inRecording,
							})}
							onClickCapture={onOpen}
							ref={setEllipsis}
							data-action-trigger
						/>
					</>
				)}
			</div>
			{isOpened && (
				<AssignableDropdown
					popperStyle={popperStyles.popper}
					ref={setPopperElement}
					initialSubView={subview}
					className={styles.itemDropdown}
					anchor={actionsRef.current || undefined}
					portal={ellipsis}
					onClose={onCloseDropdown}
					menuSections={menuSections}
					forceDesktop
					{...attributes.popper}
				/>
			)}
		</div>
	);
}
